import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import { getClientId } from '../../utils';
import { SESSION_KEY_LOCATION_ID } from '../../constants';

type Location = {
  uuid: string;
  region?: string;
  city?: string;
  name: string;
  latitude?: string;
  longitude?: string;
};

interface GetLocationsResponse {
  data: Location[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

const eventsApiUrl = import.meta.env.VITE_EVENTS_API_URL;

function getDistanceFromLatLng(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function findClosestLocation(userLat: number, userLng: number, locations: Location[]): Location | null {
  let closestLocation: Location | null = null;
  let minDistance = Number.POSITIVE_INFINITY;

  for (const location of locations) {
    const lat = Number.parseFloat(location.latitude ?? '');
    const lng = Number.parseFloat(location.longitude ?? '');
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

    const distance = getDistanceFromLatLng(userLat, userLng, lat, lng);
    if (distance < minDistance) {
      minDistance = distance;
      closestLocation = location;
    }
  }

  return closestLocation;
}

function sortLocations(a: Location, b: Location): number {
  const key = (loc: Location) => [loc.region?.trim() ?? '', loc.city?.trim() ?? '', loc.name].join('\0');
  return key(a).localeCompare(key(b));
}

function formatLocationContext(city?: string, region?: string): string {
  const parts = [city?.trim(), region?.trim()].filter(Boolean);
  return parts.join(', ');
}

function formatSelectedValue(name: string, city?: string, region?: string): string {
  const context = formatLocationContext(city, region);
  return context ? `${name} · ${context}` : name;
}

export default function LocationPickerModal({ open, onClose }: Props) {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationUUID, setSelectedLocationUUID] = useState<string>('default');
  const [isLocating, setIsLocating] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [menuWidth, setMenuWidth] = useState<number | undefined>();
  const selectRef = useRef<HTMLDivElement>(null);

  const sortedLocations = useMemo(() => [...locations].sort(sortLocations), [locations]);
  const hasValidLocation =
    selectedLocationUUID !== 'default' && locations.some((location) => location.uuid === selectedLocationUUID);

  useEffect(() => {
    if (!open) return;

    const initialLocation = localStorage.getItem(SESSION_KEY_LOCATION_ID) || 'default';
    setSelectedLocationUUID(initialLocation);
    setFeedbackMessage('');

    const fetchLocations = async () => {
      setIsLoadingLocations(true);
      try {
        const clientId = getClientId();
        if (!clientId) {
          setFeedbackMessage('Unable to load locations right now. Please choose again in a moment.');
          return;
        }

        const response = await axios.request<GetLocationsResponse>({
          url: '/locations/list/details',
          method: 'get',
          baseURL: eventsApiUrl,
          headers: { client_id: clientId },
        });

        const locationData = response.data?.data ?? [];
        setLocations(locationData);
      } catch (error) {
        setFeedbackMessage('Unable to load locations right now. Please try again shortly.');
      } finally {
        setIsLoadingLocations(false);
      }
    };

    fetchLocations();
  }, [open]);

  const handleFindMyLocation = () => {
    setFeedbackMessage('');
    if (!navigator.geolocation) {
      setFeedbackMessage('Geolocation is not supported by your browser. Please select a location manually.');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const closestLocation = findClosestLocation(position.coords.latitude, position.coords.longitude, locations);
        if (!closestLocation) {
          setFeedbackMessage('We could not match your location to an enabled campus. Please select one below.');
          setIsLocating(false);
          return;
        }

        setSelectedLocationUUID(closestLocation.uuid);
        setIsLocating(false);
      },
      () => {
        setFeedbackMessage('We could not access your location. Please allow location access or choose a location manually.');
        setIsLocating(false);
      },
    );
  };

  const handleTakeMeThere = () => {
    if (!hasValidLocation) {
      setFeedbackMessage('Please select a valid location before continuing.');
      return;
    }

    const clientId = getClientId();
    if (!clientId) {
      setFeedbackMessage('Unable to continue right now. Please refresh and try again.');
      return;
    }

    localStorage.setItem(SESSION_KEY_LOCATION_ID, selectedLocationUUID);
    window.location.href = `/?client_id=${clientId}&location_id=${selectedLocationUUID}`;
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setSelectedLocationUUID(event.target.value);
    setFeedbackMessage('');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Choose Location</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please select your location below:
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Button variant="outlined" onClick={handleFindMyLocation} disabled={isLocating || isLoadingLocations || locations.length === 0}>
            Find my location
          </Button>
          {isLocating && <CircularProgress size={18} />}
        </Box>

        {feedbackMessage && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {feedbackMessage}
          </Alert>
        )}

        <FormControl fullWidth ref={selectRef}>
          <InputLabel id="events-location-picker-label">Location</InputLabel>
          <Select
            labelId="events-location-picker-label"
            label="Location"
            value={selectedLocationUUID}
            onChange={handleSelectChange}
            disabled={isLoadingLocations}
            onOpen={() => {
              if (selectRef.current) {
                setMenuWidth(selectRef.current.clientWidth);
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: menuWidth,
                  maxWidth: menuWidth,
                },
              },
            }}
            sx={{
              '& .MuiSelect-select': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
            renderValue={(selected) => {
              if (selected === 'default') return 'Select your location';
              const loc = locations.find((location) => location.uuid === selected);
              const text = loc ? formatSelectedValue(loc.name, loc.city, loc.region) : 'Selected location';
              return (
                <Typography
                  component="span"
                  noWrap
                  title={text}
                  sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {text}
                </Typography>
              );
            }}
          >
            <MenuItem value="default">Select your location</MenuItem>
            {sortedLocations.map((location) => {
              const isSelected = selectedLocationUUID === location.uuid;
              const context = formatLocationContext(location.city, location.region);

              return (
                <MenuItem
                  key={location.uuid}
                  value={location.uuid}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    py: 1.5,
                    overflow: 'hidden',
                    maxWidth: '100%',
                    bgcolor: isSelected ? 'action.selected' : undefined,
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      mr: 1.5,
                      mt: 0.25,
                      flexShrink: 0,
                      color: isSelected ? 'primary.main' : 'action.active',
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      color={isSelected ? 'primary.main' : 'text.primary'}
                      noWrap
                      title={location.name}
                      sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {location.name}
                    </Typography>
                    {context && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        title={context}
                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {context}
                      </Typography>
                    )}
                  </Box>
                  {isSelected && (
                    <CheckIcon sx={{ ml: 1, flexShrink: 0, color: 'primary.main' }} />
                  )}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleTakeMeThere} disabled={!hasValidLocation}>
          Take me there
        </Button>
      </DialogActions>
    </Dialog>
  );
}
